/**
 * Canonical permission catalogue.
 *
 * Permissions are *seeded into the database* from this list and mapped to roles
 * in `role_permissions`, so an admin can re-map them at runtime without a code
 * change (directive §54). This file is the source of truth for which permission
 * codes exist; the database is the source of truth for who currently holds them.
 *
 * Server authorisation always reads the database. The client imports the same
 * codes purely to decide which controls to render — never as an access decision.
 */

import type { Role } from './domain';

export const PERMISSIONS = {
  // Org / master data
  ORG_VIEW: 'org.view',
  ORG_MANAGE: 'org.manage',
  MACHINE_VIEW: 'machine.view',
  MACHINE_MANAGE: 'machine.manage',

  // Checklist templates
  TEMPLATE_VIEW: 'template.view',
  TEMPLATE_MANAGE: 'template.manage',
  TEMPLATE_REVIEW: 'template.review',
  TEMPLATE_APPROVE: 'template.approve',
  TEMPLATE_PUBLISH: 'template.publish',
  SCHEDULE_MANAGE: 'schedule.manage',

  // Engineering standards
  STANDARD_VIEW: 'standard.view',
  STANDARD_MANAGE: 'standard.manage',
  /**
   * Approving a tolerance turns a recorded reading into a scored one that can
   * raise Problem Tags. Deliberately NOT granted to ADMIN by default — see
   * DEFAULT_ROLE_PERMISSIONS below.
   */
  STANDARD_APPROVE: 'standard.approve',

  // Checklist execution
  RUN_VIEW_OWN: 'run.view.own',
  RUN_VIEW_ALL: 'run.view.all',
  RUN_EXECUTE: 'run.execute',
  RUN_VERIFY: 'run.verify',
  RUN_CORRECT: 'run.correct',
  RUN_CANCEL: 'run.cancel',

  // Problem tags
  PROBLEM_VIEW: 'problem.view',
  PROBLEM_CREATE: 'problem.create',
  PROBLEM_ASSIGN: 'problem.assign',
  PROBLEM_ACT: 'problem.act',
  PROBLEM_VERIFY: 'problem.verify',
  PROBLEM_CLOSE: 'problem.close',
  PROBLEM_REJECT: 'problem.reject',

  // OPL
  OPL_VIEW: 'opl.view',
  OPL_CREATE: 'opl.create',
  OPL_EDIT: 'opl.edit',
  OPL_SUBMIT: 'opl.submit',
  OPL_REVIEW: 'opl.review',
  OPL_APPROVE: 'opl.approve',
  OPL_PUBLISH: 'opl.publish',

  // Training / competency
  TRAINING_VIEW_OWN: 'training.view.own',
  TRAINING_VIEW_ALL: 'training.view.all',
  TRAINING_COMPLETE_OWN: 'training.complete.own',
  TRAINING_ASSIGN: 'training.assign',
  TRAINING_ASSESS: 'training.assess',

  // Reporting
  DASHBOARD_OPERATOR: 'dashboard.operator',
  DASHBOARD_SUPERVISOR: 'dashboard.supervisor',
  DASHBOARD_COACH: 'dashboard.coach',
  DASHBOARD_MAINTENANCE: 'dashboard.maintenance',
  DASHBOARD_MANAGEMENT: 'dashboard.management',
  REPORT_VIEW: 'report.view',
  REPORT_EXPORT: 'report.export',

  // Administration
  USER_VIEW: 'user.view',
  USER_MANAGE: 'user.manage',
  ROLE_MANAGE: 'role.manage',
  AUDIT_VIEW: 'audit.view',
  SETTINGS_MANAGE: 'settings.manage',
  BACKUP_MANAGE: 'backup.manage',
  SYNC_INSPECT: 'sync.inspect',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS = Object.values(PERMISSIONS) as PermissionCode[];

export const PERMISSION_DESCRIPTIONS: Record<PermissionCode, string> = {
  'org.view': 'View factories, areas and lines',
  'org.manage': 'Create and edit factories, areas and lines',
  'machine.view': 'View machines and components',
  'machine.manage': 'Create and edit machines and components',
  'template.view': 'View checklist templates',
  'template.manage': 'Create and edit checklist template drafts',
  'template.review': 'Review a submitted checklist version',
  'template.approve': 'Approve a reviewed checklist version',
  'template.publish': 'Publish an approved checklist template version',
  'schedule.manage': 'Assign checklist schedules to machines',
  'standard.view': 'View engineering standards',
  'standard.manage': 'Draft and edit engineering standards',
  'standard.approve':
    'Approve an engineering tolerance and put it into service (enables scoring)',
  'run.view.own': 'View own checklist runs',
  'run.view.all': 'View all checklist runs',
  'run.execute': 'Perform checklists',
  'run.verify': 'Verify or reject submitted checklists',
  'run.correct': 'Correct a submitted answer (audited, original preserved)',
  'run.cancel': 'Cancel a checklist run',
  'problem.view': 'View problem tags',
  'problem.create': 'Raise problem tags',
  'problem.assign': 'Assign problem tags to a person or department',
  'problem.act': 'Record diagnosis, corrective and preventive actions',
  'problem.verify': 'Verify corrective action effectiveness',
  'problem.close': 'Close a verified problem tag',
  'problem.reject': 'Reject a problem tag',
  'opl.view': 'View published One-Point Lessons',
  'opl.create': 'Create One-Point Lessons',
  'opl.edit': 'Edit draft One-Point Lessons',
  'opl.submit': 'Submit an OPL for review',
  'opl.review': 'Review a submitted OPL',
  'opl.approve': 'Approve an OPL',
  'opl.publish': 'Publish an approved OPL',
  'training.view.own': 'View own training and competency record',
  'training.view.all': 'View training records for all operators',
  'training.complete.own': 'Mark own OPL training as viewed/completed',
  'training.assign': 'Assign training and OPLs to operators',
  'training.assess': 'Record competency assessments and AM step certification',
  'dashboard.operator': 'Access the operator dashboard',
  'dashboard.supervisor': 'Access the supervisor dashboard',
  'dashboard.coach': 'Access the AM coach dashboard',
  'dashboard.maintenance': 'Access the maintenance dashboard',
  'dashboard.management': 'Access the management dashboard',
  'report.view': 'View reports',
  'report.export': 'Export report data',
  'user.view': 'View users',
  'user.manage': 'Create, edit and deactivate users',
  'role.manage': 'Manage roles and role permissions',
  'audit.view': 'View the audit log',
  'settings.manage': 'Change system settings and configuration lists',
  'backup.manage': 'Create, download and restore database backups',
  'sync.inspect': 'Inspect the offline sync log',
};

const P = PERMISSIONS;

/**
 * Permissions deliberately withheld from ADMIN by default.
 *
 * These change what the system does to production equipment or product, not how
 * it is administered. An administrator can still grant one to themselves — but
 * that is a deliberate, audited act rather than something they inherit.
 */
export const WITHHELD_FROM_ADMIN: PermissionCode[] = [P.STANDARD_APPROVE];

/**
 * ADMIN's permissions, listed explicitly.
 *
 * why not `ALL_PERMISSIONS.filter(...)`: an exclusion rule quietly grants every
 * *future* permission to ADMIN, so the next sensitive capability someone adds
 * lands in the administrator role without anyone deciding that. Listing them
 * means a new permission is not granted until a human adds it here — and the
 * `permissions.test.ts` guard fails the build until they classify it either
 * here or in WITHHELD_FROM_ADMIN.
 */
export const ADMIN_PERMISSIONS: PermissionCode[] = [
  // Org and equipment
  P.ORG_VIEW,
  P.ORG_MANAGE,
  P.MACHINE_VIEW,
  P.MACHINE_MANAGE,

  // Checklist templates
  P.TEMPLATE_VIEW,
  P.TEMPLATE_MANAGE,
  P.TEMPLATE_REVIEW,
  P.TEMPLATE_APPROVE,
  P.TEMPLATE_PUBLISH,
  P.SCHEDULE_MANAGE,

  // Engineering standards — may draft and edit, may NOT approve into service
  P.STANDARD_VIEW,
  P.STANDARD_MANAGE,

  // Checklist execution
  P.RUN_VIEW_OWN,
  P.RUN_VIEW_ALL,
  P.RUN_EXECUTE,
  P.RUN_VERIFY,
  P.RUN_CORRECT,
  P.RUN_CANCEL,

  // Problem tags
  P.PROBLEM_VIEW,
  P.PROBLEM_CREATE,
  P.PROBLEM_ASSIGN,
  P.PROBLEM_ACT,
  P.PROBLEM_VERIFY,
  P.PROBLEM_CLOSE,
  P.PROBLEM_REJECT,

  // OPL
  P.OPL_VIEW,
  P.OPL_CREATE,
  P.OPL_EDIT,
  P.OPL_SUBMIT,
  P.OPL_REVIEW,
  P.OPL_APPROVE,
  P.OPL_PUBLISH,

  // Training
  P.TRAINING_VIEW_OWN,
  P.TRAINING_VIEW_ALL,
  P.TRAINING_COMPLETE_OWN,
  P.TRAINING_ASSIGN,
  P.TRAINING_ASSESS,

  // Dashboards and reporting
  P.DASHBOARD_OPERATOR,
  P.DASHBOARD_SUPERVISOR,
  P.DASHBOARD_COACH,
  P.DASHBOARD_MAINTENANCE,
  P.DASHBOARD_MANAGEMENT,
  P.REPORT_VIEW,
  P.REPORT_EXPORT,

  // Administration
  P.USER_VIEW,
  P.USER_MANAGE,
  P.ROLE_MANAGE,
  P.AUDIT_VIEW,
  P.SETTINGS_MANAGE,
  P.BACKUP_MANAGE,
  P.SYNC_INSPECT,
];

/**
 * Default role → permission mapping, applied at seed time.
 * Derived from directive §7 and proposal §5 (project organisation).
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, PermissionCode[]> = {
  /** Explicit by design — see ADMIN_PERMISSIONS above and ADR-0015. */
  ADMIN: ADMIN_PERMISSIONS,

  ENGINEERING: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.MACHINE_MANAGE,
    P.TEMPLATE_VIEW,
    P.TEMPLATE_MANAGE,
    P.TEMPLATE_REVIEW,
    P.TEMPLATE_APPROVE,
    P.RUN_VIEW_ALL,
    P.PROBLEM_VIEW,
    P.PROBLEM_CREATE,
    P.PROBLEM_ACT,
    P.PROBLEM_VERIFY,
    P.OPL_VIEW,
    P.OPL_CREATE,
    P.OPL_EDIT,
    P.OPL_SUBMIT,
    P.STANDARD_VIEW,
    P.STANDARD_MANAGE,
    P.STANDARD_APPROVE,
    P.DASHBOARD_MAINTENANCE,
    P.REPORT_VIEW,
    P.REPORT_EXPORT,
  ],

  OPERATOR: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.TEMPLATE_VIEW,
    P.RUN_VIEW_OWN,
    P.RUN_EXECUTE,
    P.PROBLEM_VIEW,
    P.PROBLEM_CREATE,
    P.OPL_VIEW,
    P.OPL_CREATE, // operators author OPLs in the pilot (AM Step 3)
    P.OPL_EDIT,
    P.OPL_SUBMIT,
    P.TRAINING_VIEW_OWN,
    P.TRAINING_COMPLETE_OWN,
    P.DASHBOARD_OPERATOR,
  ],

  SUPERVISOR: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.TEMPLATE_VIEW,
    P.TEMPLATE_REVIEW,
    P.STANDARD_VIEW,
    P.SCHEDULE_MANAGE,
    P.RUN_VIEW_OWN,
    P.RUN_VIEW_ALL,
    P.RUN_EXECUTE,
    P.RUN_VERIFY,
    P.RUN_CORRECT,
    P.RUN_CANCEL,
    P.PROBLEM_VIEW,
    P.PROBLEM_CREATE,
    P.PROBLEM_ASSIGN,
    P.PROBLEM_ACT,
    P.PROBLEM_VERIFY,
    P.PROBLEM_CLOSE,
    P.PROBLEM_REJECT,
    P.OPL_VIEW,
    P.OPL_REVIEW,
    P.TRAINING_VIEW_ALL,
    P.TRAINING_ASSIGN,
    P.DASHBOARD_OPERATOR,
    P.DASHBOARD_SUPERVISOR,
    P.REPORT_VIEW,
    P.REPORT_EXPORT,
  ],

  AM_COACH: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.TEMPLATE_VIEW,
    P.TEMPLATE_MANAGE,
    P.TEMPLATE_REVIEW,
    P.TEMPLATE_APPROVE,
    P.TEMPLATE_PUBLISH,
    P.STANDARD_VIEW,
    P.STANDARD_MANAGE,
    P.SCHEDULE_MANAGE,
    P.RUN_VIEW_ALL,
    P.RUN_VERIFY,
    P.PROBLEM_VIEW,
    P.PROBLEM_CREATE,
    P.PROBLEM_VERIFY,
    P.OPL_VIEW,
    P.OPL_CREATE,
    P.OPL_EDIT,
    P.OPL_SUBMIT,
    P.OPL_REVIEW,
    P.OPL_APPROVE,
    P.OPL_PUBLISH,
    P.TRAINING_VIEW_ALL,
    P.TRAINING_ASSIGN,
    P.TRAINING_ASSESS,
    P.DASHBOARD_COACH,
    P.DASHBOARD_SUPERVISOR,
    P.REPORT_VIEW,
    P.REPORT_EXPORT,
  ],

  MAINTENANCE: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.MACHINE_MANAGE,
    P.TEMPLATE_VIEW,
    P.STANDARD_VIEW,
    P.RUN_VIEW_ALL,
    P.PROBLEM_VIEW,
    P.PROBLEM_CREATE,
    P.PROBLEM_ACT,
    P.PROBLEM_ASSIGN,
    P.OPL_VIEW,
    P.OPL_CREATE,
    P.OPL_EDIT,
    P.OPL_SUBMIT,
    P.DASHBOARD_MAINTENANCE,
    P.REPORT_VIEW,
  ],

  QUALITY_SAFETY: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.TEMPLATE_VIEW,
    P.TEMPLATE_REVIEW,
    P.TEMPLATE_APPROVE,
    P.STANDARD_VIEW,
    P.RUN_VIEW_ALL,
    P.PROBLEM_VIEW,
    P.PROBLEM_CREATE,
    P.PROBLEM_VERIFY,
    P.OPL_VIEW,
    P.OPL_REVIEW,
    P.OPL_APPROVE,
    P.TRAINING_VIEW_ALL,
    P.DASHBOARD_SUPERVISOR,
    P.REPORT_VIEW,
    P.REPORT_EXPORT,
  ],

  MANAGEMENT: [
    P.ORG_VIEW,
    P.MACHINE_VIEW,
    P.TEMPLATE_VIEW,
    P.STANDARD_VIEW,
    P.RUN_VIEW_ALL,
    P.PROBLEM_VIEW,
    P.OPL_VIEW,
    P.TRAINING_VIEW_ALL,
    P.DASHBOARD_MANAGEMENT,
    P.REPORT_VIEW,
    P.REPORT_EXPORT,
  ],
};
